// Abschnitt der linken Lead-Spalte: schlanke Kopfzeile (Titel, Zähler, Aktionen), einklappbar, keine Karte.
// Der Zustand (auf/zu) bleibt je Browser gemerkt.
import { useState, type ReactNode } from "react";
import { Icon, type IconName } from "../Icon";
import "../../styles/lead.css";

function gemerkt(id: string, standardZu: boolean): boolean {
  try {
    const w = localStorage.getItem(`crm-abschnitt-${id}`);
    return w === null ? standardZu : w === "zu";
  } catch {
    return standardZu;
  }
}

interface Props {
  id: string;
  titel: string;
  zahl?: number | null;
  aktionen?: ReactNode;
  standardZu?: boolean;
  /** Symbol vor dem Titel (wie die kleinen Symbole der Close-Karten) */
  icon?: IconName;
  /** Nichts darin: nur eine schmale Zeile mit „+“, bis etwas da ist. */
  leer?: boolean;
  /** Bei leer: statt `aktionen` ein „+“, das den Abschnitt aufklappt. */
  leerPlus?: boolean;
  /** Immer ausgeklappt (ein früher gemerktes Zuklappen gilt nicht), z. B. Follow-ups, sobald eines offen ist. */
  immerAuf?: boolean;
  /** Die „+“-Aktionen bleiben auch zugeklappt bzw. leer sichtbar (Follow-ups, Opportunities, Kontakte; Dilsar, 18.10.2026). */
  aktionenImmer?: boolean;
  /** Zu-Zustand von außen (nicht im Browser gemerkt), z. B. „Über“: bei jedem geöffneten Lead wieder offen. */
  zuGesteuert?: boolean;
  onZuGesteuert?: (zu: boolean) => void;
  children: ReactNode;
}

export function Abschnitt({ id, titel, zahl, aktionen, standardZu = false, icon, leer = false, leerPlus = false, immerAuf = false, aktionenImmer = false, zuGesteuert, onZuGesteuert, children }: Props) {
  const [zu, setZu] = useState(() => gemerkt(id, standardZu));
  const [leerOffen, setLeerOffen] = useState(false);
  // Leere Bereiche (Design-Runde 2, Must-have 8): nur zugeklappt, mit Chevron und 0. Aufklappen, dann anlegen.
  // Der Zustand gilt nur für diesen Besuch und wird nicht gemerkt (leer bleibt zu, bis man ihn öffnet).
  const gesteuert = zuGesteuert !== undefined;
  const eingeklappt = immerAuf && !leer ? false : leer ? !leerOffen : gesteuert ? zuGesteuert : zu;
  const umschalten = () => {
    if (immerAuf && !leer) return;
    if (leer) {
      setLeerOffen((o) => !o);
      return;
    }
    if (gesteuert) {
      onZuGesteuert?.(!zuGesteuert);
      return;
    }
    const neu = !zu;
    setZu(neu);
    try {
      localStorage.setItem(`crm-abschnitt-${id}`, neu ? "zu" : "auf");
    } catch {
      /* egal */
    }
  };
  void leerPlus;
  return (
    <section className={`ls-abschnitt${eingeklappt ? " ls-abschnitt--zu" : ""}${leer ? " ls-abschnitt--leer" : ""}`} aria-label={titel}>
      <div className="ls-abschnitt__kopf">
        <button type="button" className={`ls-abschnitt__titel${leer && !leerOffen ? " ls-abschnitt__titel--still" : ""}`} onClick={umschalten} aria-expanded={!eingeklappt} aria-disabled={immerAuf && !leer ? true : undefined} style={immerAuf && !leer ? { cursor: "default" } : undefined}>
          <Icon name={eingeklappt ? "chevron-rechts" : "chevron-unten"} groesse={12} className="ls-abschnitt__pfeil" />
          {icon ? <Icon name={icon} groesse={13} className="ls-abschnitt__symbol" /> : null}
          <span>{titel}</span>
          {leer ? <span className="ls-abschnitt__zahl">0</span> : zahl !== undefined && zahl !== null ? <span className="ls-abschnitt__zahl">{zahl}</span> : null}
        </button>
        {aktionen && (!eingeklappt || aktionenImmer) ? <div className="ls-abschnitt__aktionen">{aktionen}</div> : null}
      </div>
      {eingeklappt ? null : <div className="ls-abschnitt__inhalt">{children}</div>}
    </section>
  );
}
